/**
 * tutorSettingsViewModel — ce que les paramètres du tuteur lisent dans le store.
 *
 * Patron A : le compte connecté (`currentUser.object`, que `updateTutorAccount` remplace au succès) et
 * les données statiques (les types de notification, les liens des stores). Les valeurs rendues sont
 * celles du COMPTE ; ce que le tuteur modifie avant d'enregistrer vit dans l'état de l'écran
 * (`useTutorSettingsState`), qui les recouvre. Seules les notifications déjà basculées entrent en
 * argument (`notificationDraft`, `null` tant que rien n'a bougé), lu par sa propre entrée.
 */
import { createSelector } from "reselect"

import {
    COUNTRY_OPTIONS,
    toAddressSettings,
    toNotificationSettings,
    type ApiNotificationSettings,
    type ApiNotificationType,
} from "../../../adapters/student/Account/studentSettingsAdapter"
import { toTutorGeneralSettings, type ApiTutorSettingsUser } from "../../../adapters/tutor/Account/tutorSettingsAdapter"
import {
    type UINotificationSetting,
    type UISelectOption,
    type UIStudentAddressSettings,
} from "../../../ui/types/studentAccount"
import { type UITutorGeneralSettings } from "../../../ui/types/tutorCompte"
import { selectAccount } from "../../shared/displayedStudent"

export type TutorSettingsViewModel = {
    /** Le compte n'est pas encore là : chaque page montre son squelette. */
    loading: boolean
    general: UITutorGeneralSettings | null
    address: UIStudentAddressSettings | null
    countries: UISelectOption[]
    /** `null` tant que le compte n'est pas là. */
    notifications: UINotificationSetting[] | null
    /** Les fiches de l'app dans les stores, servies par le serveur (sinon celles du binaire). */
    storeUrls: { ios: string | null; android: string | null }
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectUser = (state: ReduxState): ApiTutorSettingsUser | undefined => selectAccount(state)
const selectNotificationTypes = (state: ReduxState): ApiNotificationType[] | undefined =>
    state?.data?.staticData?.notifications
const selectIosUrl = (state: ReduxState): string | null => state?.data?.staticData?.ios_app_url ?? null
const selectAndroidUrl = (state: ReduxState): string | null => state?.data?.staticData?.android_app_url ?? null
const selectNotificationDraft = (_state: ReduxState, draft: ApiNotificationSettings | null) => draft

export const tutorSettingsViewModel = createSelector(
    [selectUser, selectNotificationTypes, selectIosUrl, selectAndroidUrl, selectNotificationDraft],
    (user, notificationTypes, ios, android, draft): TutorSettingsViewModel => ({
        loading: user == null,
        general: user ? toTutorGeneralSettings(user) : null,
        address: user ? toAddressSettings({ address: user.address }) : null,
        countries: COUNTRY_OPTIONS,
        // `SettingsConfigurationScreen` : les types des rôles du compte, le même filtre que l'élève.
        notifications: user
            ? toNotificationSettings(notificationTypes, user.roles, draft ?? user.notification_settings)
            : null,
        storeUrls: { ios, android },
    })
)
